import React, { useState, useEffect, useCallback, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import { posthog, initPostHog, setOrganizationContext } from './lib/posthog';
import { setOrganizationTag } from './lib/sentry';
import { AppLayout } from './components/AppLayout';
import { AnalystLayout } from './components/AnalystLayout';
// WeeklyReportPage deprecated - functionality merged into OverviewPage
import { DebugPanel } from './components/DebugPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { exportCreativesToCSV } from './utils/csv-export.js';
import { useSessionStorage } from './hooks/useSessionStorage.js';
import { testServerConnection } from './utils/test-connection.js';
import { ThemeProvider } from 'next-themes';
import { logError } from './utils/logger.js';

// Auth imports - kept static as they're lightweight and needed immediately
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AnalystRoute from './components/auth/AnalystRoute';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import HubCallbackPage from './pages/auth/HubCallbackPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ConfirmPage from './pages/auth/ConfirmPage';

// Public marketing/policy pages (no auth, linked from platform API applications)
import AboutPage from './pages/public/AboutPage';
import PrivacyPage from './pages/public/PrivacyPage';
import TermsPage from './pages/public/TermsPage';

// Wrapper that auto-reloads the page once when a dynamic import fails (stale chunk after deploy).
// Each chunk gets its OWN retry key (module-eval order is stable), so a failure in one chunk
// can't suppress the one-shot retry of another. While reloading, render the spinner (not null).
let chunkRetryCounter = 0;
function lazyWithRetry(importFn: () => Promise<any>) {
  const key = `chunk-retry:${chunkRetryCounter++}`;
  return lazy(async () => {
    try {
      const module = await importFn();
      sessionStorage.removeItem(key);
      return module;
    } catch (error) {
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return { default: () => <PageLoader /> };
      }
      sessionStorage.removeItem(key);
      throw error;
    }
  });
}

// Lazy load heavy dashboard pages (includes Recharts, etc.)
const OverviewPage = lazyWithRetry(() => import('./components/OverviewPage').then(m => ({ default: m.OverviewPage })));
const PaidMediaBreakdownPage = lazyWithRetry(() => import('./components/PaidMediaBreakdownPage').then(m => ({ default: m.PaidMediaBreakdownPage })));
const MetaDashboardPage = lazyWithRetry(() => import('./components/MetaDashboardPage').then(m => ({ default: m.MetaDashboardPage })));
const GoogleDashboardPage = lazyWithRetry(() => import('./components/GoogleDashboardPage').then(m => ({ default: m.GoogleDashboardPage })));
const PinterestDashboardPage = lazyWithRetry(() => import('./components/PinterestDashboardPage').then(m => ({ default: m.PinterestDashboardPage })));
const SettingsPage = lazyWithRetry(() => import('./pages/settings/SettingsPage'));
const TeamPage = lazyWithRetry(() => import('./pages/settings/TeamPage'));
const AdminDashboardPage = lazyWithRetry(() => import('./pages/admin/AdminDashboardPage'));
const AnalystHomePage = lazyWithRetry(() => import('./pages/analyst/AnalystHomePage'));
const BudgetPage = lazyWithRetry(() => import('./pages/budget/BudgetPage'));

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Component to redirect root to user's default organization
function RootRedirect() {
  const { currentOrganization, organizations, hasInitialLoad } = useOrganization();
  const [timedOut, setTimedOut] = useState(false);

  // Add timeout to prevent infinite loading - redirect to login after 15 seconds
  useEffect(() => {
    if (!hasInitialLoad) {
      const timer = setTimeout(() => {
        console.error('RootRedirect: Organization loading timed out after 15 seconds');
        setTimedOut(true);
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [hasInitialLoad]);

  // If timed out, redirect to login
  if (timedOut) {
    return <Navigate to="/login" state={{ error: 'Loading timed out' }} replace />;
  }

  // If still loading initial org data, show spinner
  if (!hasInitialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const defaultOrg = currentOrganization || organizations[0];
  if (defaultOrg?.slug) {
    return <Navigate to={`/${defaultOrg.slug}`} replace />;
  }

  // No organizations - redirect to login
  return <Navigate to="/login" replace />;
}

// Component to conditionally render DebugPanel for Batman users only
function BatmanDebugPanel({ isVisible, onToggle }: { isVisible: boolean; onToggle: () => void }) {
  const { isBatman } = useAuth();

  if (!isBatman) {
    return null;
  }

  return <DebugPanel isVisible={isVisible} onToggle={onToggle} />;
}

// Component to register global properties on route changes
// PostHog automatically captures $pageview and $pageleave events
// This adds org context and dashboard type to ALL events
function PageViewTracker() {
  const location = useLocation();
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    const path = location.pathname;
    let dashboardType = 'unknown';

    if (path.endsWith('/meta-dashboard')) {
      dashboardType = 'meta';
    } else if (path.endsWith('/google-dashboard')) {
      dashboardType = 'google';
    } else if (path.endsWith('/pinterest-dashboard')) {
      dashboardType = 'pinterest';
    } else if (path.endsWith('/paid-media-breakdown')) {
      dashboardType = 'paid_media_breakdown';
    } else if (path.includes('/settings/team')) {
      dashboardType = 'team_settings';
    } else if (path.includes('/settings')) {
      dashboardType = 'settings';
    } else if (
      path === '/login' ||
      path === '/signup' ||
      path === '/forgot-password' ||
      path === '/reset-password' ||
      path.startsWith('/auth/')
    ) {
      dashboardType = 'auth';
    } else if (path.match(/^\/[^/]+\/?$/)) {
      dashboardType = 'overview';
    }

    // Register global properties added to ALL events (including auto-captured $pageview)
    posthog.register({
      org_id: currentOrganization?.id,
      org_slug: currentOrganization?.slug,
      dashboard_type: dashboardType,
    });

    // Associate the org with PostHog group analytics + tag Sentry events per-tenant so
    // errors and analytics can be filtered by client organization.
    if (currentOrganization?.id && currentOrganization?.slug) {
      setOrganizationContext(currentOrganization.id, currentOrganization.slug);
      setOrganizationTag(currentOrganization.id, currentOrganization.slug);
    }
  }, [location.pathname, currentOrganization?.id, currentOrganization?.slug]);

  return null;
}

// Route-level error isolation: a crash in one page shows a recoverable fallback and
// keeps the app shell + other routes alive. Auto-resets when the route changes.
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary name={`route:${location.pathname}`} resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  );
}

interface FilterState {
  search: string;
  dateRange: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  minSpend?: number;
  minImpressions?: number;
  adStatus?: string;
  includeDynamicFeed?: boolean;
  excludeNewAds?: boolean;
  onlyXyzManaged?: boolean;
  customStartDate?: string; // YYYY-MM-DD
  customEndDate?: string; // YYYY-MM-DD
}

const DEFAULT_FILTERS: FilterState = {
  search: '',
  dateRange: 'last_week_sun_sat', // Default to week to date
  status: 'ACTIVE',
  sortBy: 'roas',
  sortOrder: 'desc',
  minSpend: 0.01, // Hide zero-spend ads by default
  adStatus: 'ACTIVE',
  includeDynamicFeed: false, // Default to filtering out dynamic feed campaigns
  excludeNewAds: false,
  onlyXyzManaged: true
};

export default function App() {
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isDebugVisible, setIsDebugVisible] = useState(false);

  // Persistent filters in session storage
  const [filters, setFilters] = useSessionStorage('meta-dashboard-filters', DEFAULT_FILTERS);

  // Initialize PostHog analytics
  useEffect(() => {
    initPostHog();
  }, []);

  // Counts for header display - these will be populated by individual pages
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  
  // Handle hard refresh: reset date range to 'yesterday'
  useEffect(() => {
    // Check if this is a hard refresh (reload) vs client-side navigation
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const navType = navEntries[0]?.type;

    if (navType === 'reload') {
      console.log('[App] Hard refresh detected, resetting date range to last_week_sun_sat');
      setFilters(prev => ({
        ...prev,
        dateRange: 'last_week_sun_sat',
        customStartDate: undefined,
        customEndDate: undefined,
      }));
    } else if (!filters.dateRange) {
      // Fallback: ensure default date range if missing
      console.log('[App] No date range found, setting default to last_week_sun_sat');
      setFilters(prev => ({
        ...prev,
        dateRange: 'last_week_sun_sat'
      }));
    }
  }, []); // Run only on mount

  // Global handlers for unhandled promise rejections AND synchronous errors thrown
  // outside React's render tree (event handlers, timers, 3rd-party scripts). Both feed
  // Sentry via logError so these large error classes are no longer invisible.
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      logError('App.unhandledRejection', 'Unhandled promise rejection detected', err, {
        type: 'unhandled_promise_rejection',
      });
      // Only swallow genuine abort/timeout aborts (narrow match, not any message containing "timeout").
      if (event.reason?.name === 'AbortError' || event.reason?.name === 'TimeoutError') {
        event.preventDefault();
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      logError(
        'App.windowError',
        event.message || 'Uncaught error',
        event.error instanceof Error ? event.error : new Error(event.message || 'Uncaught error'),
        { type: 'window_error', filename: event.filename, lineno: event.lineno }
      );
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleWindowError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleWindowError);
    };
  }, []);

  // Test connection on first load (only in debug mode)
  useEffect(() => {
    // Only run connection test if explicitly requested with debug parameter
    if (window.location.search.includes('debug=true')) {
      testServerConnection()
        .then(result => {
          console.log('Connection test completed:', result);
          if (!result.success) {
            console.warn('[WARNING] Server connection test failed:', result.error || result.healthError || 'Unknown error');
          }
        })
        .catch(error => {
          console.warn('Connection test failed:', error);
          // Don't show toast for connection test failures
        });
    }
  }, []);

  // These will be set by individual pages
  const [handleExportCSV, setHandleExportCSV] = useState<(() => void) | null>(null);
  const [handleRefresh, setHandleRefresh] = useState<(() => void) | null>(null);

  // Stable callback for setting handlers
  const onSetHandlers = useCallback((exportFn: () => void, refreshFn: () => void) => {
    setHandleExportCSV(() => exportFn);
    setHandleRefresh(() => refreshFn);
  }, []);

  // Stable callback for counts change
  const onCountsChange = useCallback((total: number, filtered: number) => {
    setTotalCount(total);
    setFilteredCount(filtered);
  }, []);

  // Default handlers for pages that don't implement these
  const defaultExportCSV = () => {
    console.info('[INFO] Export functionality: Export will work based on the current page data');
  };

  const defaultRefresh = () => {
    console.info('[INFO] Refresh triggered: This will refresh the current page data');
  };

  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light">
        <PostHogProvider client={posthog}>
          <AuthProvider>
          <OrganizationProvider>
              <Router>
                <PageViewTracker />
                <div className="min-h-screen bg-background">
                  <Routes>
                  {/* Public auth routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/auth/confirm" element={<ConfirmPage />} />
                  <Route path="/auth/hub-callback" element={<HubCallbackPage />} />

                  {/* Public policy pages */}
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />

                  {/* Root redirect to default organization */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <RootRedirect />
                      </ProtectedRoute>
                    }
                  />

                  {/* Analyst cross-org dashboard (outside org context) */}
                  <Route element={<AnalystRoute />}>
                    <Route path="/analyst" element={<AnalystLayout />}>
                      <Route index element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><AnalystHomePage /></Suspense></RouteErrorBoundary>} />
                    </Route>
                  </Route>

                  {/* Protected routes with org slug */}
                  <Route element={<ProtectedRoute />}>
                    <Route
                      path="/:orgSlug/*"
                      element={
                        <AppLayout
                          filters={filters}
                          onFiltersChange={setFilters}
                          onRefresh={handleRefresh || defaultRefresh}
                          onExportCSV={handleExportCSV || defaultExportCSV}
                          isLoading={isLoading}
                          totalCount={totalCount}
                          filteredCount={filteredCount}
                        />
                      }
                    >
                      {/* Overview Dashboard */}
                      <Route index element={
                        <RouteErrorBoundary><Suspense fallback={<PageLoader />}>
                          <OverviewPage
                            filters={filters}
                            onFiltersChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
                          />
                        </Suspense></RouteErrorBoundary>
                      } />

                      {/* Paid Media Breakdown (custom reports for orgs with has_custom_reports flag) */}
                      <Route
                        path="paid-media-breakdown"
                        element={
                          <RouteErrorBoundary><Suspense fallback={<PageLoader />}>
                            <PaidMediaBreakdownPage
                              filters={filters}
                              onFiltersChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
                            />
                          </Suspense></RouteErrorBoundary>
                        }
                      />

                      {/* Meta Dashboard */}
                      <Route
                        path="meta-dashboard"
                        element={
                          <RouteErrorBoundary><Suspense fallback={<PageLoader />}>
                            <MetaDashboardPage
                              filters={filters}
                              isLoading={isLoading}
                              setIsLoading={setIsLoading}
                              onCountsChange={onCountsChange}
                              onSetHandlers={onSetHandlers}
                              onFiltersChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
                            />
                          </Suspense></RouteErrorBoundary>
                        }
                      />

                      {/* Google Dashboard */}
                      <Route
                        path="google-dashboard"
                        element={
                          <RouteErrorBoundary><Suspense fallback={<PageLoader />}>
                            <GoogleDashboardPage
                              filters={filters}
                              isLoading={isLoading}
                              setIsLoading={setIsLoading}
                              onCountsChange={onCountsChange}
                              onSetHandlers={onSetHandlers}
                              onFiltersChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
                            />
                          </Suspense></RouteErrorBoundary>
                        }
                      />

                      {/* Pinterest Dashboard */}
                      <Route
                        path="pinterest-dashboard"
                        element={
                          <RouteErrorBoundary><Suspense fallback={<PageLoader />}>
                            <PinterestDashboardPage
                              filters={filters}
                              isLoading={isLoading}
                              setIsLoading={setIsLoading}
                              onCountsChange={onCountsChange}
                              onSetHandlers={onSetHandlers}
                              onFiltersChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
                            />
                          </Suspense></RouteErrorBoundary>
                        }
                      />

                      {/* Budget Management */}
                      <Route path="budgets" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><BudgetPage /></Suspense></RouteErrorBoundary>} />

                      {/* Weekly Report - redirect to Overview (deprecated) */}
                      <Route
                        path="weekly-report"
                        element={<Navigate to={`..`} replace />}
                      />

                      {/* Settings */}
                      <Route path="settings" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><SettingsPage /></Suspense></RouteErrorBoundary>} />
                      <Route path="settings/team" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><TeamPage /></Suspense></RouteErrorBoundary>} />

                      {/* Admin (Batman-only) */}
                      <Route path="admin" element={<RouteErrorBoundary><Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense></RouteErrorBoundary>} />
                    </Route>
                  </Route>

                  {/* Legacy redirects - send old URLs to root for org resolution */}
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/meta-dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/google-dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/pinterest-dashboard" element={<Navigate to="/" replace />} />
                  <Route path="/settings" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Debug Panel - Batman only */}
                <BatmanDebugPanel
                  isVisible={isDebugVisible}
                  onToggle={() => setIsDebugVisible(!isDebugVisible)}
                />
              </div>
            </Router>
          </OrganizationProvider>
        </AuthProvider>
        </PostHogProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}